import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next'; // ADDED T-TEST
import { CustomLabelRenderer } from './CustomLabelRenderer'; // AB ADDED
import { JsonForms, withJsonFormsControlProps, withJsonFormsCellProps } from '@jsonforms/react';
import { materialRenderers, materialCells } from '@jsonforms/material-renderers';
import { rankWith, isControl, uiTypeIs, Resolve } from '@jsonforms/core';
import { Autocomplete, TextField } from '@mui/material';
import debounce from 'lodash.debounce';
import isEqual from 'lodash/isEqual';
import AppContext from '../utils/context';
import { callRpc } from '../utils/rpc';

const buildParams = (args = [], ctx = {}, data = {}) =>
  args.reduce((p, k) => {
    p[k] = k === 'metadata' ? data : ctx[k] ?? null;
    return p;
  }, {});

// ADDED T-TEST
const localizeUiSchema = (uischema, t) => {
  if (Array.isArray(uischema)) {
    return uischema.map(e => localizeUiSchema(e, t));
  }
  if (typeof uischema === 'object' && uischema !== null) {
    const copy = { ...uischema };
    if (copy.label) copy.label = t(copy.label);
    if (copy.text) copy.text = t(copy.text);
    if (copy.description) copy.description = t(copy.description);
    if (copy.elements) copy.elements = localizeUiSchema(copy.elements, t);
    if (copy.options && typeof copy.options === 'object') {
      copy.options = localizeUiSchema(copy.options, t);
    }
    return copy;
  }
  return uischema;
};
//--------------

// Composant de sélection basé sur optionsList fourni
const DynSelectBase = ({ schema, uischema, data, handleChange, path, errors, enabled, visible, label }) => {
  if (!visible) return null;
  const options = schema?.items?.oneOf || [];
  return (
    <Autocomplete
      multiple={true}
      options={options}
      getOptionLabel={option => option.title}
      isOptionEqualToValue={(option, value) => option.const === value}
      disabled={!enabled}
      value={options.filter(o => (data || []).includes(o.const))
      }
      onChange={(_, value) =>
        handleChange(path, value.map(v => v.const))
      }
      renderInput={params => (
        <TextField
          {...params}
          label={label}
          error={!!errors}
          helperText={errors || schema.tooltipText}
        />
      )}
    />
  );
};

const DynSelectControl = withJsonFormsControlProps(DynSelectBase);
const DynSelectCell = withJsonFormsCellProps(DynSelectBase);

// Testers pour JsonForms
const DynSelectControlTester = rankWith(
  10,
  (uischema, schema) => {
    const resolvedSchema = Resolve.schema(schema, uischema.scope);
    return (
      isControl(uischema) &&
      uischema?.options?.autocomplete === true &&
      resolvedSchema.uniqueItems === true &&
      resolvedSchema.type === 'array' &&
      Array.isArray(resolvedSchema?.items?.oneOf)
    );
  }
);
const DynSelectCellTester = rankWith(
  10,
  (uischema, schema) =>
    uiTypeIs('Control')(uischema) &&
    uischema?.options?.autocomplete === true && 
    schema.uniqueItems === true && 
    schema.type === 'array' &&
    Array.isArray(schema?.items?.oneOf)
);

// Composant principal
export default function CustomJsonForm({
  schema,
  uischema,
  contextVars = {},
  data: initialData = {},
  onChange,
  validationMode,
  config,
  ...rest
}) {
  const { database } = useContext(AppContext);  
  const { t } = useTranslation(); // ADDED T-TEST
  const [formData, setFormData] = useState(initialData);
  const ctxKey = JSON.stringify(contextVars);
  const memoCtx = useMemo(() => contextVars, [ctxKey]);

  // Synchronisation parent → interne
  useEffect(() => {
    if (!isEqual(initialData, formData)) setFormData(initialData);
  }, [initialData]);

  // Fonction de mise à jour de formData + appel onChange
  const applyPatch = patch => {
    if (!patch || !Object.keys(patch).length) return;
    setFormData(prev => {
      const merged = { ...prev, ...patch };
      if (isEqual(prev, merged)) return prev;
      onChange?.({ data: merged });
      return merged;
    });
  };

  // Initialisation via x-corey.functions.form_init
  useEffect(() => {
    const initDef = schema['x-corey']?.functions?.form_init;
    // if (initDef && !Object.keys(initialData).length) {
    if (initDef) {
      (async () => {
        const { function: fn, arguments: args } = initDef;
        const { data: result } = await callRpc(database, fn, buildParams(args, memoCtx, {}));
        applyPatch(result);
      })();
    }
  }, [schema, database, memoCtx]);

  // Format (déclenche form_change avec debounce)
  const changeDef = schema['x-corey']?.functions?.form_change;
  const debouncedChange = useMemo(
    () =>
      debounce(async data => {
        if (!changeDef) return;
        const { function: fn, arguments: args } = changeDef;
        const { data: result } = await callRpc(database, fn, buildParams(args, memoCtx, data));
        applyPatch(result);
      }, 300),
    [changeDef, database, memoCtx]
  );
  useEffect(() => () => debouncedChange.cancel(), [debouncedChange]);

  // Gestion locale des changements
  const handleChange = ({ data, errors }) => {
    setFormData(data);
    onChange?.({ data, errors });
    debouncedChange(data);
  };

  const translatedUiSchema = useMemo(() => localizeUiSchema(uischema, t), [uischema, t]); // ADDED T-TEST

  
  return (
    <JsonForms
      schema={schema}
      //uischema={uischema}
      uischema={translatedUiSchema} // ADDED T-TEST
      data={formData}
      onChange={handleChange}
      validationMode={validationMode}
      config={config}     
      renderers={[
        ...materialRenderers,
        CustomLabelRenderer, // AB ADDED
        { tester: DynSelectControlTester, renderer: DynSelectControl }
      ]}
      cells={[
        ...materialCells,
        { tester: DynSelectCellTester, cell: DynSelectCell }
      ]}
      {...rest}
    />
  );
}
